import { LocalizationProvider } from '@localization/LocalizationProvider';
import styles from './FunctionsComponent.module.scss';

import { CdfGrid } from '@commonComponents/data/grid/CdfGrid/CdfGrid.tsx';
import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import { GridApi } from 'ag-grid-enterprise';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { RowSelectionMode } from '@models/enums/RowSelectionMode';
import { PermissionProvider } from '@auth/PermissionProvider.tsx';
import { useInjectReducers } from '@hooks/useInjectReducers.ts';
import { ApiCallType } from '@api/ApiCalls.ts';
import { gridColumnDefinitions } from './grid-column-definitions';
import { DynamicStore } from '@state/interfaces/idynamic-store';
import classNames from 'classnames';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '@commonComponents/core/ErrorFallback';
import { logger } from '@utils/logging-utils';
import type { EntityDialogCloseResult } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';

// MUI components
import { ThemeProvider } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import brzTheme from '@brz/brz-global-mui-theme';
import { Typography } from '@mui/material';
import { useFunctionsPermissions } from '@hooks/permissions/useFunctionsPermissions';
import { FunctionBase } from '@models/DomainModels/Master/function-base';
import { FunctionDetailDialogWrapper } from './FunctionDetailDialog';

/**
 * Props interface for FunctionsComponent
 */
interface FunctionsComponentProps {
  /** Organization ID for permission checks and API calls */
  readonly organizationId: string;
  /** Redux store for state management */
  readonly store: DynamicStore | undefined;
}

const extendedAG_GRID_LOCALE_DE = {
  ...AG_GRID_LOCALE_DE,
  ariaHeaderSelection: ''
};

/**
 * Component for managing functions
 * Displays a grid of functions with CRUD operations
 */
function FunctionsComponent({ organizationId, store }: Readonly<FunctionsComponentProps>) {
  const { writePermission } = useFunctionsPermissions(organizationId);

  const { t, i18n } = useTranslation(['functions', 'common']);
  const [localeText, setLocaleText] = useState(AG_GRID_LOCALE_EN);
  const [gridKey, setGridKey] = useState(0);
  const gridApiRef = useRef<GridApi | null>(null);
  const gridRef = useRef<{
    refresh: () => void;
    addCreatedItem: (item: FunctionBase) => void;
    updateModifiedItem: (item: FunctionBase) => void;
  }>(null);
  const selectedIdsRef = useRef<string[]>([]);

  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedFunctionData, setSelectedFunctionData] = useState<FunctionBase | undefined>(undefined);

  /**
   * Handle language change and update the grid locale
   */
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const newLocaleText = lng === 'de' ? extendedAG_GRID_LOCALE_DE : AG_GRID_LOCALE_EN;
      setLocaleText(newLocaleText);
      setGridKey(prevKey => prevKey + 1);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  /**
   * Grid ready callback to store the grid API reference
   */
  const onGridReady = useCallback((params: { api: GridApi }) => {
    gridApiRef.current = params.api;
  }, []);

  /**
   * Handle clicking on a function profile
   * @param data The function data
   */
  const handleProfileClick = useCallback((data: FunctionBase) => {
    if (!data?.id) {
      logger.warn('FunctionsComponent', 'Profile click with invalid data', data);
      return;
    }

    selectedIdsRef.current = [data.id];
    setSelectedFunctionData(data);
    setProfileDialogOpen(true);
  }, []);

  /**
   * Handle profile dialog close and update the grid using transactions
   */
  const handleProfileClose = useCallback((result: EntityDialogCloseResult<FunctionBase>) => {
    setProfileDialogOpen(false);

    if (result.action === 'created' && result.data && gridRef.current) {
      gridRef.current.addCreatedItem(result.data);
    } else if (result.action === 'updated' && result.data && gridRef.current) {
      gridRef.current.updateModifiedItem(result.data);
    }

    setSelectedFunctionData(undefined);
  }, []);

  /**
   * Memoized column definitions to prevent unnecessary re-renders
   */
  const columnDefs = useCallback(() => gridColumnDefinitions({ t }), [t]);

  /**
   * Handle clicking the add button to create a new function
   */
  const handleAddButtonClick = useCallback(() => {
    setSelectedFunctionData(undefined);
    setProfileDialogOpen(true);
  }, []);

  return (
    <Grid className={styles.functionsComponent}>
      <Typography variant="h4" component="h1">
        {t('title')}
      </Typography>
      <Grid className={styles.content}>
        <Grid className={classNames(styles.grid)}>
          <CdfGrid<FunctionBase>
            ref={gridRef}
            apiCallType={ApiCallType.FetchFunctions}
            key={gridKey}
            columnDefs={columnDefs()}
            gridOptions={{ onGridReady, localeText }}
            rowSelection={RowSelectionMode.Multiple}
            showAddButton={writePermission}
            showDeleteButton={writePermission}
            showDeleteButtonInGrid={writePermission}
            onAddClick={handleAddButtonClick}
            addButtonText={t('addButtonText')}
            onDoubleClick={event => event.data && handleProfileClick(event.data)}
          />
        </Grid>
      </Grid>
      {profileDialogOpen && (
        <FunctionDetailDialogWrapper
          functionData={selectedFunctionData}
          open={profileDialogOpen}
          onClose={handleProfileClose}
          organizationId={organizationId}
          store={store}
        />
      )}
    </Grid>
  );
}

/**
 * Wrapper component for FunctionsComponent
 * Provides necessary context providers and error boundaries
 */
export function FunctionsComponentWrapper({ organizationId, store }: Readonly<FunctionsComponentProps>) {
  const reducerInjected = useInjectReducers(store, organizationId);
  if (!reducerInjected) {
    return null;
  }

  return (
    <ThemeProvider theme={brzTheme}>
      <LocalizationProvider>
        {/* Type assertion used to handle DynamicStore compatibility with Redux Provider */}
        <Provider store={store}>
          <PermissionProvider>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <FunctionsComponent organizationId={organizationId} store={store} />
            </ErrorBoundary>
          </PermissionProvider>
        </Provider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
